import { Channel } from 'scbk-services/config';
import { create } from 'zustand';
import { MenuItem, MenuTreeItem, SiteMapTreeItem } from '../../types/menu/menu';
import { MenuLoader } from '../managers/MenuLoader';

/**
 * MenuStoreState
 */
interface MenuStoreState {
    /**
     * 메뉴로드 초기화 여부
     */
    initialized: boolean;

    /**
     * 메뉴정보 초기화를 수행한다.
     * @returns
     */
    clear: () => void;

    /**
     * 메뉴 트리정보
     */
    menuTree: MenuTreeItem;

    /**
     * 사이트맵 트리정보
     */
    siteMapTree: SiteMapTreeItem;

    /**
     * flat 메뉴 목록
     */
    flattenMenuList: MenuItem[] | undefined;

    /**
     * 현재 진입 메뉴정보
     */
    currentMenu: MenuItem | null | undefined;

    /**
     * 현재 진입 메뉴정보를 store에 설정한다.
     * @param menu 메뉴정보
     * @returns
     */
    setCurrentMenuItem: (menu: MenuItem) => void;

    /**
     * 메뉴ID로 메뉴정보를 조회한다.
     * @param menuId 메뉴ID
     * @returns
     */
    find: (menuId: string) => MenuItem | null | undefined;

    /**
     * 메뉴정보를 로드한다.
     * @param channel 현재 접속채널
     * @param raw 메뉴정보 raw 데이터 (xml)
     * @returns
     */
    load: (channel: string | Channel, raw: string, siteMap: string) => Promise<void>;

    /**
     * 메뉴 url로 메뉴정보를 조회한다.
     * @param url 메뉴url
     * @returns
     */
    findByPath: (url: string) => MenuItem | null | undefined;

    /**
     * 메뉴파라미터 값을 획득한다.
     * @param menuId  메뉴ID
     * @param parameterName  파라미터명
     * @returns  메뉴ID, 파라미터명으로 획득한 파라미터값
     */
    getMenuParameter: (menuId: string, parameterName: string) => string | undefined;

    /**
     * 메뉴필드 값을 획득한다.
     * @param menuId 메뉴ID
     * @param fieldName 필드명
     * @returns 메뉴ID, 필드명으로 획득한 필드값
     */
    getMenuField: (menuId: string, fieldName: string) => string | undefined;
}

/**
 *
 */
const useMenuStore = create<MenuStoreState>((set, get) => ({
    initialized: false,
    menuTree: {},
    flattenMenuList: [],
    currentMenu: null,
    siteMapTree: {},
    load: async (channel: string | Channel, raw: string, siteRaw: string) => {
        const { initialized } = get();
        if (initialized) {
            console.log('### useMenuStore 초기화 완료되어 skip 처리 ');
            return;
        }

        const start = performance.now();
        try {
            const menuLoader = new MenuLoader(channel, raw, siteRaw);
            const menuTree = await menuLoader.load();
            const flattenMenuList = await menuLoader.flatMenuTree(Object.values(menuTree));
            const siteMapTree = await menuLoader.siteMapLoad(flattenMenuList);

            console.log('useMenuStore > load - menuTree : ', {
                channel: channel,
                menuTree,
                siteMapTree,
            });

            set({
                initialized: true,
                menuTree,
                flattenMenuList,
                siteMapTree,
            });
        } finally {
            const elapsed = performance.now() - start;
            console.log(
                `### useMenuStore menuLoad elapsed time ${elapsed} ${elapsed.toFixed(2)}ms`,
            );
        }
    },
    clear: () => {
        set({
            initialized: false,
            menuTree: {},
        });
    },
    setCurrentMenuItem: (menu) => {
        set({ currentMenu: menu });
    },

    find: (menuId: string) => {
        return get().flattenMenuList?.find((v) => v.id === menuId);
    },
    findByPath: (url: string) => {
        const normalizedUrlString = (url: string) => {
            let _url = url;
            const idx = _url.indexOf('?');
            if (idx > -1) {
                _url = _url.substring(0, idx);
            }
            return _url;
        };
        return get().flattenMenuList?.find((v) => {
            const _url = normalizedUrlString(v.url);
            const target = normalizedUrlString(url);
            return _url === target;
        });
    },

    getMenuParameter: (menuId, parameterName) => {
        const menuInfo = get().flattenMenuList?.find((v) => v.id === menuId);
        if (!menuInfo) {
            return '';
        }

        if (!menuInfo.parameter) {
            return '';
        }

        const value = menuInfo.parameter[parameterName];
        if (!value) {
            return '';
        }

        return value;
    },

    getMenuField: (menuId, fieldName) => {
        const menuInfo = get().flattenMenuList?.find((v) => v.id === menuId);
        if (!menuInfo) {
            return '';
        }

        if (!menuInfo.fields) {
            return '';
        }

        const value = menuInfo.fields[fieldName];
        if (!value) {
            return '';
        }

        return value;
    },
}));

export { useMenuStore };
